import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent, DrawerCloseEvent } from '@theme/theme-drawer';

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, and the installments CTA close-on-click.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.#themeDrawer?.addEventListener(DrawerCloseEvent.eventName, this.#handleDrawerClose);

    // Delegated, not queried: a direct `this.querySelector('#checkout')`
    // attached inside #handleDrawerOpen looked right and tested right in
    // isolation, but failed in the one flow that matters most — add to
    // cart, drawer auto-opens, cart contents re-render moments later via
    // the section rendering fetch, which replaces this exact button with a
    // fresh node that was never listening. <cart-drawer-component> itself
    // is what survives that re-render (confirmed against
    // #handleCartLinesUpdate below, which already relies on the same
    // assumption), so the listener lives here permanently and matches by
    // closest() at the moment of the event instead of holding a reference
    // to a node that might not exist by the time it's clicked.
    //
    // click, not pointerdown: pointerdown fires before the press finishes,
    // and closing this dialog synchronously hides the button mid-gesture —
    // most browsers then never dispatch the click at all, since its target
    // disappeared before the press was released. That shipped once: it
    // silently ate every tap on "Check out", which is a worse bug than the
    // one it was meant to fix. click only fires after the full gesture
    // completes, by which point Razorpay's own handler — attached closer to
    // the button, so it runs first during bubbling — has already had every
    // chance a normal click would give it.
    this.addEventListener('click', this.#handleCheckoutClick);

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.#themeDrawer?.removeEventListener(DrawerCloseEvent.eventName, this.#handleDrawerClose);
    this.removeEventListener('click', this.#handleCheckoutClick);
    window.removeEventListener('blur', this.#handleWindowBlur);
    this.#injectionObserver?.disconnect();
    this.#iframeResizeObserver?.disconnect();
  }

  /**
   * Handles the theme-drawer opening — updates sticky state and wires up the installments CTA.
   */
  #handleDrawerOpen = () => {
    this.#updateStickyState();

    // Close cart drawer when installments CTA is clicked to avoid overlapping dialogs.
    // Re-queried on every open so it survives cart content re-renders that
    // replace the shopify-payment-terms shadow root.
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });

    // The remaining three mechanisms below are defense-in-depth for a
    // provider that renders as a genuine separate express button — Shop
    // Pay, PayPal, Google Pay — should one ever be enabled here. Kept for
    // that case even though the reported bug turned out to have a
    // different, more direct cause.
    // Same problem, a second source: accelerated checkout buttons
    // (content_for_additional_checkout_buttons — Shop Pay, PayPal, Razorpay
    // Magic Checkout and similar) open their own modal or redirect on click.
    // That modal is a normal z-indexed overlay, not a <dialog> in the
    // browser's top layer the way this drawer is, so it always renders
    // underneath — reported live as Razorpay's checkout appearing to do
    // nothing until the shopper closes the cart by hand and finds it
    // waiting behind. click, deferred a tick — see #handleCheckoutClick
    // above for why a synchronous close here is not safe, even inside a
    // click handler that fires after the provider's own. #closeInstantly bypasses the
    // slide-out animation and calls dialog.close() directly — the point
    // isn't a tidy exit, it's vacating the top layer before the provider's
    // overlay tries to occupy the same space. Re-queried on every open for
    // the same reason as the installments CTA above: a cart update can
    // replace this container's contents entirely.
    this.querySelector('.additional-checkout-buttons')?.addEventListener(
      'click',
      () => setTimeout(() => this.#closeInstantly(), 0),
      { once: true, capture: true }
    );

    // Covers the case the listener above cannot: a checkout button rendered
    // inside a cross-origin iframe never dispatches its click to this
    // document, so pointerdown on the container never fires. A click
    // there still moves focus into the iframe, which this document sees
    // as its own window losing focus — checking what took it lets this
    // fire only for an iframe actually inside the checkout-buttons
    // container, not the chat widget or anything else on the page doing
    // the same thing for an unrelated reason.
    window.addEventListener('blur', this.#handleWindowBlur);

    // Third, and most general: react to the checkout overlay itself
    // appearing anywhere in the document, regardless of what triggered it.
    this.#watchForCheckoutOverlay();
  };

  #handleDrawerClose = () => {
    window.removeEventListener('blur', this.#handleWindowBlur);
    this.#injectionObserver?.disconnect();
    this.#iframeResizeObserver?.disconnect();
  };

  /**
   * The actual, direct cause of the reported bug, confirmed from a screen
   * recording: this store has no separate express/accelerated checkout
   * button — .additional-checkout-buttons renders empty — so the button a
   * shopper taps is the plain "Check out" submit button every cart has.
   * Razorpay Magic Checkout intercepts that submit and renders its own
   * full-screen flow, already fully loaded, while this drawer's dialog sat
   * in the browser's top layer for the whole recording: "Processing"
   * flashed once on the button and then it just sat there, cart still
   * showing, for several seconds, until the shopper closed it by hand and
   * found checkout waiting underneath the entire time.
   *
   * The close itself is deferred a tick past the click, not run inside this
   * handler — setTimeout, after every click listener on this event,
   * including Razorpay's own, has already run to completion. Closing this
   * synchronously here worked in every manual test, right up until a real
   * checkout attempt broke entirely: DOM changes made *during* a shared
   * click dispatch can still land ahead of a later listener's own work in
   * ways a synthetic test click doesn't reproduce. A tick's delay is not
   * perceptible against the multi-second wait this exists to fix, and it
   * costs nothing to spend it making sure nothing sharing this click ever
   * has to run against a dialog this code is in the middle of closing.
   *
   * @param {MouseEvent} event
   */
  #handleCheckoutClick = (event) => {
    if (event.target instanceof Element && event.target.closest('#checkout')) {
      setTimeout(() => this.#closeInstantly(), 0);
    }
  };

  #handleWindowBlur = () => {
    const active = document.activeElement;
    if (active instanceof HTMLIFrameElement && this.querySelector('.additional-checkout-buttons')?.contains(active)) {
      this.#closeInstantly();
    }
  };

  /** @type {MutationObserver | null} */
  #injectionObserver = null;

  /** @type {ResizeObserver | null} */
  #iframeResizeObserver = null;

  /**
   * Catches what the two listeners above cannot: they both depend on
   * catching the shopper's *click*, which assumes the button lives where
   * expected and behaves like a normal button or a same-container iframe.
   * A payment provider that renders its checkout as its own top-level
   * overlay — appended straight to <body>, not nested inside
   * .additional-checkout-buttons at all, which is how several actually
   * work — slips past both. This reacts to the result instead of the
   * input: the instant any new iframe lands in the document while the
   * drawer is open, close it. A checkout overlay is built from an iframe
   * for PCI isolation industry-wide (Razorpay, Shop Pay, PayPal, Google
   * Pay's sheet); nothing else the storefront does adds one while the cart
   * is open, so this cannot mistake ordinary cart activity for a checkout
   * opening.
   */
  #watchForCheckoutOverlay() {
    this.#injectionObserver?.disconnect();
    this.#iframeResizeObserver?.disconnect();

    // Below is the mechanism this was originally built for — a fresh
    // iframe appearing. Confirmed live it is not the one Razorpay actually
    // uses: its checkout iframe (.razorpay-checkout-frame) is already in
    // the document from page load, sized 0x0 and hidden. "Check out"
    // doesn't add it, it resizes it — 0x0 to the full viewport — which a
    // childList mutation observer structurally cannot see, since nothing
    // was added or removed. Kept for any provider that does create a fresh
    // iframe on demand; ResizeObserver below is what actually catches
    // Razorpay.
    this.#injectionObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (!(node instanceof Element)) continue;
          if (node.tagName === 'IFRAME' || node.querySelector('iframe')) {
            this.#closeInstantly();
            return;
          }

          // A provider that inserts its iframe already-collapsed and
          // expands it moments later — the same pattern as Razorpay's
          // pre-existing one, just newly added instead of present from
          // load — needs the same resize watch, not just the insertion
          // check above.
          const inserted = node.tagName === 'IFRAME' ? [node] : [...node.querySelectorAll('iframe')];
          inserted.forEach((frame) => this.#iframeResizeObserver?.observe(frame));
        }
      }
    });
    this.#injectionObserver.observe(document.body, { childList: true, subtree: true });

    // The confirmed real mechanism: watch every iframe already on the page
    // for growing past tracking-pixel size. 200px is comfortably above the
    // 4x4 web-pixel sandbox iframe this storefront already runs (seen live
    // alongside Razorpay's), comfortably below any real checkout surface.
    this.#iframeResizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        if (width > 200 && height > 200) {
          this.#closeInstantly();
          return;
        }
      }
    });
    for (const frame of document.querySelectorAll('iframe')) {
      this.#iframeResizeObserver.observe(frame);
    }
  }

  /**
   * Exits the dialog's top-layer stacking immediately rather than through
   * the theme-drawer's animated close, so a checkout overlay opening on the
   * same interaction never has to render behind it, even for a frame.
   * `dialog.close()` first, then the theme-drawer's own close() for
   * attribute, focus and event bookkeeping — its `if (panel.open)` guard
   * already skips the animation once the panel is closed, so this is safe
   * to call in sequence rather than a workaround around it.
   */
  #closeInstantly() {
    this.#dialog?.close();
    this.#themeDrawer?.close();
    this.#injectionObserver?.disconnect();
    this.#iframeResizeObserver?.disconnect();
  }

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen = this.hasAttribute('auto-open') && event.action === 'add' && !this.#themeDrawer?.isOpen;

    // When the event originates inside an open MODAL <dialog> (e.g. quick-add),
    // defer the auto-open until that dialog's native `close` fires so its focus
    // restoration runs first — otherwise we'd capture the wrong
    // `#previouslyFocused`. Non-modal dialogs (e.g. the hotspot preview) don't
    // close on add and don't move focus, so `:modal` excludes them.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    // Adds from the product page wait for the Add to Bag tick to finish before
    // the drawer slides over the button.
    const fromProductPage =
      /** @type {any} */ (event).context === 'product' ||
      (event.target instanceof Element && Boolean(event.target.closest('product-form-component')));
    const openDelay = fromProductPage && !sourceModal ? 750 : 0;

    if (shouldAutoOpen && !sourceModal && !this.#isCartEmpty() && !openDelay) {
      this.#themeDrawer?.open();
    }

    event.promise
      ?.then(({ detail }) => {
        const settle = () => requestAnimationFrame(() => this.#updateStickyState());

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAndSettle, { once: true });
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  #isCartEmpty() {
    return Boolean(this.querySelector('.cart-drawer--empty'));
  }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
